import { useState, useEffect, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n||0).toLocaleString("en-IN");

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00", bg:"#f3f9ec" },
  { id:"amazon",   name:"Amazon",   color:"#c45000", bg:"#fff3e8" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0", bg:"#ebf2ff" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c", bg:"#fff0f4" },
];

const GST_RATES   = [0,5,12,18,28];
const CATEGORIES  = ["Belts","Wallets","Bags","Accessories","Footwear","Apparel","Other"];
const BELT_ATTRS  = [
  { name:"Size",     values:["Free Size","28","30","32","34","36","38","40","42","44"] },
  { name:"Color",    values:["Black","Brown","Tan","Navy","Reversible Black/Brown"] },
  { name:"Material", values:["Full-grain Leather","Genuine Leather","Bonded Leather","Synthetic"] },
  { name:"Buckle",   values:["Auto-lock","Pin","Ratchet","Double Prong"] },
];

const STATUS_COLOR = {
  active:     { color:"#15803d", bg:"#f0fdf4", label:"Active" },
  inactive:   { color:"#d97706", bg:"#fffbeb", label:"Inactive" },
  not_listed: { color:"#6b7280", bg:"#f3f4f6", label:"Not Listed" },
};

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb", whiteSpace:"nowrap" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", verticalAlign:"middle", fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" },
  modal:   { background:"#fff", borderRadius:12, padding:24, width:720, maxWidth:"96vw", maxHeight:"92vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  kpi:     { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background:bg, color, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600, whiteSpace:"nowrap" }}>{label}</span>
);

const EMPTY_PRODUCT = {
  name:"", brand:"", description:"", shortDesc:"",
  category:"Belts", subCategory:"", tags:"",
  hsn:"42033000", gstRate:18,
  weight:0, length:0, breadth:0, height:0,
  basePrice:0, mrp:0, costPrice:0,
  channelPricing:{ shopify:0, amazon:0, flipkart:0, myntra:0 },
  attributeSchema:[], images:"", notes:"",
  autoGenerateVariants: false,
};

export default function PagePIM({ toast }) {
  const [tab,        setTab]        = useState("catalog");
  const [aiImage,    setAiImage]    = useState(null);      // { base64, preview, file }
  const [aiLoading,  setAiLoading]  = useState(false);
  const [aiResult,   setAiResult]   = useState(null);
  const [aiPlatform, setAiPlatform] = useState("all");
  const [extraInfo,  setExtraInfo]  = useState({ sku:"", mrp:"", costPrice:"", hsn:"42033000", category:"Belts", color:"", size:"", material:"Genuine Leather", brand:"Leather Patta" });
  const [products,   setProducts]   = useState([]);
  const [stats,      setStats]      = useState({});
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [catFilter,  setCatFilter]  = useState("");
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState(CATEGORIES);

  // modals
  const [showModal,  setShowModal]  = useState(false);
  const [editProduct,setEditProduct]= useState(null);
  const [form,       setForm]       = useState(EMPTY_PRODUCT);
  const [activeTab,  setActiveTab]  = useState("basic");
  const [detailProd, setDetailProd] = useState(null);
  const [publishing, setPublishing] = useState("");

  // bulk upload
  const [bulkData,   setBulkData]   = useState([]);
  const [uploading,  setUploading]  = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit:20, ...(search?{search}:{}), ...(catFilter?{category:catFilter}:{}) });
      const [prodData, statsData, catData] = await Promise.all([
        api("/pim/products?" + params.toString()),
        api("/pim/stats"),
        api("/pim/categories"),
      ]);
      setProducts(prodData.products || []);
      setTotalPages(prodData.pages  || 1);
      setStats(statsData.stats      || {});
      const allCats = [...new Set([...CATEGORIES, ...(catData.categories||[])])];
      setCategories(allCats);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, [toast, page, search, catFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => { setEditProduct(null); setForm(EMPTY_PRODUCT); setActiveTab("basic"); setShowModal(true); };

  const openEdit = async (id) => {
    try {
      const data = await api("/pim/products/" + id);
      const p    = data.product;
      setEditProduct(p);
      setForm({
        name:p.name, brand:p.brand, description:p.description, shortDesc:p.shortDesc,
        category:p.category, subCategory:p.subCategory, tags:(p.tags||[]).join(", "),
        hsn:p.hsn, gstRate:p.gstRate,
        weight:p.weight, length:p.length, breadth:p.breadth, height:p.height,
        basePrice:p.basePrice, mrp:p.mrp, costPrice:p.costPrice,
        channelPricing:p.channelPricing||{shopify:0,amazon:0,flipkart:0,myntra:0},
        attributeSchema:p.attributeSchema||[],
        images:(p.images||[]).join("\n"), notes:p.notes,
        autoGenerateVariants:false,
      });
      setActiveTab("basic");
      setShowModal(true);
    } catch(e) { toast(e.message, "error"); }
  };

  const saveProduct = async () => {
    if (!form.name) { toast("Enter product name", "error"); return; }
    try {
      const payload = {
        ...form,
        tags:   form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        images: form.images ? form.images.split("\n").map((u) => u.trim()).filter(Boolean) : [],
      };
      if (editProduct) {
        await api("/pim/products/" + editProduct._id, { method:"PUT", body:JSON.stringify(payload) });
        toast("Product updated ✓", "success");
      } else {
        await api("/pim/products", { method:"POST", body:JSON.stringify(payload) });
        toast("Product created ✓", "success");
      }
      setShowModal(false);
      loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  const deleteProduct = async (id) => {
    try {
      await api("/pim/products/" + id, { method:"DELETE" });
      toast("Product deleted ✓", "success");
      loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  const generateVariants = async (id) => {
    try {
      const data = await api("/pim/products/" + id + "/generate-variants", { method:"POST", body:JSON.stringify({}) });
      toast(data.count + " variants generated ✓", "success");
      loadAll();
      if (detailProd?._id === id) setDetailProd(data.product);
    } catch(e) { toast(e.message, "error"); }
  };

  const publishToShopify = async (id) => {
    setPublishing(id);
    try {
      const data = await api("/pim/products/" + id + "/publish-shopify", { method:"POST", body:JSON.stringify({}) });
      toast(data.message, "success");
      loadAll();
    } catch(e) { toast(e.message, "error"); }
    finally { setPublishing(""); }
  };

  // add attribute to schema in form
  const addAttr = (preset) => {
    if (preset) {
      setForm((f) => ({ ...f, attributeSchema: [...(f.attributeSchema||[]), { ...preset }] }));
    } else {
      setForm((f) => ({ ...f, attributeSchema: [...(f.attributeSchema||[]), { name:"", values:[] }] }));
    }
  };

  const updateAttr = (i, key, val) => {
    setForm((f) => {
      const schema = [...(f.attributeSchema||[])];
      schema[i] = { ...schema[i], [key]: val };
      return { ...f, attributeSchema: schema };
    });
  };

  const removeAttr = (i) => {
    setForm((f) => ({ ...f, attributeSchema: f.attributeSchema.filter((_,idx) => idx!==i) }));
  };

  // bulk upload parse
  const handleBulkFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const XLSX = await import("xlsx");
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf);
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      setBulkData(rows);
      toast(rows.length + " rows loaded ✓", "success");
    } catch(e) { toast("Failed to read file: " + e.message, "error"); }
  };

  const submitBulk = async () => {
    if (!bulkData.length) { toast("No data to upload", "error"); return; }
    setUploading(true);
    try {
      const data = await api("/pim/bulk-upload", { method:"POST", body:JSON.stringify({ products: bulkData }) });
      toast(data.message, "success");
      if (data.errors?.length) toast(data.errors.length + " rows had errors", "error");
      setBulkData([]);
      loadAll();
      setTab("catalog");
    } catch(e) { toast(e.message, "error"); }
    finally { setUploading(false); }
  };

  const handleAiImageUpload = (file) => {
    if (!file || !file.type.startsWith("image/")) { toast("Upload an image file", "error"); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      setAiImage({ base64: e.target.result.split(",")[1], preview: e.target.result, file });
      setAiResult(null);
    };
    reader.readAsDataURL(file);
  };

  const generateCatalog = async () => {
    if (!aiImage) { toast("Upload a product image first", "error"); return; }
    setAiLoading(true);
    try {
      const PLATFORMS_CONTEXT = {
        shopify:  { maxTitle:255, maxDesc:5000, keywords:true,  hsn:false, style:"SEO-rich, storytelling, brand voice" },
        amazon:   { maxTitle:200, maxDesc:2000, keywords:true,  hsn:true,  style:"Feature-bullet format, keyword-rich, compliance-focused" },
        flipkart: { maxTitle:255, maxDesc:2000, keywords:true,  hsn:true,  style:"Specification-heavy, bullet points, size/material focused" },
        myntra:   { maxTitle:255, maxDesc:1500, keywords:true,  hsn:false, style:"Fashion-forward, style-focused, occasion-based" },
        meesho:   { maxTitle:255, maxDesc:1000, keywords:false, hsn:false, style:"Simple, value-focused, vernacular-friendly" },
      };

      const platforms = aiPlatform === "all" ? Object.keys(PLATFORMS_CONTEXT) : [aiPlatform];
      const extraCtx  = Object.entries(extraInfo).filter(([,v])=>v).map(([k,v])=>k+": "+v).join(", ");

      // Build platform schema string without nested backticks
      const platformSchema = platforms.map((p) =>
        '"' + p + '": { "title": "string (max ' + PLATFORMS_CONTEXT[p].maxTitle + ' chars)", "description": "string", "bulletPoints": ["string"], "keywords": ["string"], "metaTitle": "string", "metaDescription": "string", "category": "string", "tags": ["string"] }'
      ).join(",\n    ");

      const promptText = "You are a product listing expert for Indian ecommerce platforms. Analyse this product image and generate optimised catalog content.\n\n" +
        "Brand: Leather Patta (Vasudeva Enterprises, Ghaziabad)\n" +
        "Extra product info: " + (extraCtx || "None") + "\n" +
        "Platforms needed: " + platforms.join(", ") + "\n\n" +
        "Generate catalog content optimised for each platform:\n" +
        "- Shopify: SEO-rich storytelling, 150-200 word description, meta title/description\n" +
        "- Amazon: Bullet-point features, search keywords, Amazon India style guide\n" +
        "- Flipkart: Specification table style, technical details prominent\n" +
        "- Myntra: Fashion-forward, occasion-based, style-conscious audience\n" +
        "- Meesho: Simple Hindi-friendly language, value proposition, reseller-friendly\n\n" +
        "Return ONLY a JSON object (no markdown):\n" +
        "{\n" +
        '  "productAnalysis": { "detectedProduct": "string", "color": "string", "material": "string", "style": "string", "suggestedHsn": "string", "suggestedCategory": "string", "keyFeatures": ["string"], "targetAudience": "string" },\n' +
        '  "platforms": {\n    ' + platformSchema + "\n  }\n}";

      // Route through backend proxy to avoid CORS + keep API key secure
      const data = await api("/ai/catalog", {
        method: "POST",
        body: JSON.stringify({
          imageBase64: aiImage.base64,
          mediaType:   aiImage.file.type || "image/jpeg",
          prompt:      promptText,
        })
      });

      const text  = data.result || "{}";
      const clean = text.replace(/```json|```/g, "").trim();
      setAiResult(JSON.parse(clean));
      toast("✅ AI catalog generated for " + platforms.length + " platform(s)", "success");
    } catch(e) {
      console.error("AI Catalog error:", e);
      toast("Generation failed: " + (e.message || "Check browser console for details"), "error");
    }
    setAiLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast("Copied ✓", "success"));
  };

  const saveToProduct = async () => {
    if (!aiResult) return;
    const pl = aiResult.platforms;
    const firstPl = Object.values(pl)[0];
    const analysis = aiResult.productAnalysis;
    try {
      const payload = {
        name:        firstPl?.title || analysis?.detectedProduct || "New Product",
        description: firstPl?.description || "",
        category:    analysis?.suggestedCategory || extraInfo.category,
        hsn:         analysis?.suggestedHsn || extraInfo.hsn,
        tags:        firstPl?.tags || [],
        sku:         extraInfo.sku,
        mrp:         parseFloat(extraInfo.mrp) || 0,
        costPrice:   parseFloat(extraInfo.costPrice) || 0,
        brand:       extraInfo.brand,
        color:       analysis?.color || extraInfo.color,
        material:    analysis?.material || extraInfo.material,
        platformContent: pl,
      };
      await api("/pim/products", { method:"POST", body:JSON.stringify(payload) });
      toast("Product saved to catalog ✓", "success");
      load();
      setTab("catalog");
    } catch(e) { toast("Failed to save: "+e.message, "error"); }
  };

  return (
    <div>
      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
        {[
          { label:"Total Products",   value:stats.total       || 0, color:"#2563eb" },
          { label:"With Variants",    value:stats.withVariants|| 0, color:"#7c3aed" },
          { label:"Live on Shopify",  value:stats.onShopify   || 0, color:"#5a8a00" },
          { label:"Live on Amazon",   value:stats.onAmazon    || 0, color:"#c45000" },
        ].map((k) => (
          <div key={k.label} style={S.kpi}>
            <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
        {[{id:"catalog",label:"📦 Catalog"},{id:"ai_catalog",label:"🤖 AI Catalog Generator"},{id:"bulk",label:"⬆ Bulk Upload"},{id:"detail",label:detailProd?"📋 "+detailProd.name.slice(0,20)+"…":"📋 Product Detail",disabled:!detailProd}].map((t) => (
          <button key={t.id} style={{ ...S.tab(tab===t.id), opacity:t.disabled?0.4:1 }} onClick={() => !t.disabled && setTab(t.id)}>{t.label}</button>
        ))}
        <div style={{ flex:1 }}/>
        <button style={{ ...S.btn(), margin:"6px 0" }} onClick={openCreate}>+ New Product</button>
      </div>

      {/* ══ CATALOG TAB ══ */}
      {tab==="catalog" && (
        <>
          {/* filters */}
          <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
            <input style={{ ...S.input, width:260, marginBottom:0 }} placeholder="Search products…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}/>
            <select style={{ ...S.input, width:160, marginBottom:0 }} value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }}>
              <option value="">All Categories</option>
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
            <span style={{ fontSize:12, color:"#9ca3af", alignSelf:"center" }}>{stats.total || 0} products</span>
          </div>

          <div style={S.card}>
            {loading ? (
              <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading…</div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["Product","Category","Variants","Base Price","Channel Prices","Listings","Actions"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p._id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            {p.images?.[0]
                              ? <img src={p.images[0]} alt="" style={{ width:36, height:36, borderRadius:6, objectFit:"cover" }}/>
                              : <div style={{ width:36, height:36, borderRadius:6, background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>📦</div>
                            }
                            <div>
                              <div style={{ fontWeight:600, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                              <div style={{ fontSize:11, color:"#9ca3af" }}>{p.brand}</div>
                            </div>
                          </div>
                        </td>
                        <td style={S.td}><Badge label={p.category||"—"} color="#374151" bg="#f3f4f6"/></td>
                        <td style={{ ...S.td, textAlign:"center" }}>
                          {p.variants?.length > 0
                            ? <span style={{ fontWeight:600, color:"#7c3aed" }}>{p.variants.length}</span>
                            : <span style={{ color:"#9ca3af" }}>—</span>}
                        </td>
                        <td style={{ ...S.td, fontWeight:600 }}>{INR(p.basePrice)}</td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                            {PLATFORMS.map((pl) => (
                              <span key={pl.id} style={{ fontSize:10, background:pl.bg, color:pl.color, padding:"1px 6px", borderRadius:4, fontWeight:600 }}>
                                {pl.name.charAt(0)}: {INR(p.channelPricing?.[pl.id]||p.basePrice)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                            {PLATFORMS.map((pl) => {
                              const st = STATUS_COLOR[p.listings?.[pl.id]?.status || "not_listed"];
                              return <span key={pl.id} style={{ fontSize:9, background:st.bg, color:st.color, padding:"1px 5px", borderRadius:4, fontWeight:600 }}>{pl.name.charAt(0)}</span>;
                            })}
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:4 }}>
                            <button style={{ ...S.btnOut, fontSize:11 }} onClick={() => { setDetailProd(p); setTab("detail"); }}>View</button>
                            <button style={{ ...S.btnOut, fontSize:11 }} onClick={() => openEdit(p._id)}>Edit</button>
                            <button style={{ ...S.btn("#5a8a00"), fontSize:11, padding:"3px 8px", opacity:publishing===p._id?0.7:1 }} onClick={() => publishToShopify(p._id)} disabled={!!publishing}>
                              {publishing===p._id ? "…" : "↑ Shopify"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {products.length === 0 && (
                      <tr><td colSpan={7} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:50 }}>
                        <div style={{ fontSize:36, marginBottom:12 }}>📦</div>
                        No products in PIM yet — add your first product or bulk upload
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {/* pagination */}
            {totalPages > 1 && (
              <div style={{ padding:"12px 16px", borderTop:"1px solid #f3f4f6", display:"flex", justifyContent:"center", gap:8 }}>
                {Array.from({ length:totalPages }, (_,i) => (
                  <button key={i} onClick={() => setPage(i+1)} style={{ width:32, height:32, borderRadius:6, border:"1px solid #d1d5db", background:page===i+1?"#F47920":"#fff", color:page===i+1?"#fff":"#374151", cursor:"pointer", fontFamily:"inherit" }}>{i+1}</button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ══ PRODUCT DETAIL TAB ══ */}
      {tab==="detail" && detailProd && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          {/* left — product info */}
          <div>
            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>{detailProd.name}</span>
                <div style={{ display:"flex", gap:6 }}>
                  <button style={S.btnOut} onClick={() => openEdit(detailProd._id)}>Edit</button>
                  <button style={{ ...S.btn("#5a8a00"), opacity:publishing===detailProd._id?0.7:1 }} onClick={() => publishToShopify(detailProd._id)} disabled={!!publishing}>
                    {publishing===detailProd._id ? "Publishing…" : "↑ Publish to Shopify"}
                  </button>
                </div>
              </div>
              <div style={{ padding:16 }}>
                {/* images */}
                {detailProd.images?.length > 0 && (
                  <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
                    {detailProd.images.map((img, i) => (
                      <img key={i} src={img} alt="" style={{ width:72, height:72, borderRadius:8, objectFit:"cover", border:"1px solid #e5e7eb" }}/>
                    ))}
                  </div>
                )}

                {/* basic info */}
                {[
                  ["Brand",       detailProd.brand],
                  ["Category",    detailProd.category + (detailProd.subCategory?" / "+detailProd.subCategory:"")],
                  ["HSN Code",    detailProd.hsn],
                  ["GST Rate",    detailProd.gstRate + "%"],
                  ["Weight",      detailProd.weight + "g"],
                  ["Dimensions",  detailProd.length+"×"+detailProd.breadth+"×"+detailProd.height+" cm"],
                  ["Tags",        (detailProd.tags||[]).join(", ")],
                ].filter(([,v]) => v && v !== "0g" && v !== "0×0×0 cm").map(([l,v]) => (
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #f3f4f6", fontSize:13 }}>
                    <span style={{ color:"#6b7280" }}>{l}</span>
                    <span style={{ fontWeight:500 }}>{v}</span>
                  </div>
                ))}

                {/* pricing */}
                <div style={{ fontWeight:600, fontSize:13, margin:"14px 0 8px" }}>Pricing</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
                  {[
                    { label:"Base Price", value:INR(detailProd.basePrice),  color:"#F47920" },
                    { label:"MRP",        value:INR(detailProd.mrp),         color:"#374151" },
                    { label:"Cost Price", value:INR(detailProd.costPrice),   color:"#6b7280" },
                    { label:"Margin",     value:detailProd.basePrice>0&&detailProd.costPrice>0 ? Math.round((detailProd.basePrice-detailProd.costPrice)/detailProd.basePrice*100)+"%" : "—", color:"#15803d" },
                  ].map(([label,value,color]) => (
                    <div key={label} style={{ background:"#f9fafb", borderRadius:7, padding:"8px 10px" }}>
                      <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>{label}</div>
                      <div style={{ fontWeight:700, color }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* channel pricing */}
                <div style={{ fontWeight:600, fontSize:13, margin:"14px 0 8px" }}>Channel Pricing</div>
                {PLATFORMS.map((pl) => (
                  <div key={pl.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"1px solid #f3f4f6" }}>
                    <span style={{ fontWeight:600, color:pl.color, fontSize:13 }}>{pl.name}</span>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontWeight:600 }}>{INR(detailProd.channelPricing?.[pl.id]||detailProd.basePrice)}</span>
                      <Badge label={STATUS_COLOR[detailProd.listings?.[pl.id]?.status||"not_listed"].label}
                        color={STATUS_COLOR[detailProd.listings?.[pl.id]?.status||"not_listed"].color}
                        bg={STATUS_COLOR[detailProd.listings?.[pl.id]?.status||"not_listed"].bg}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* right — variants */}
          <div>
            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>Variants ({detailProd.variants?.length || 0})</span>
                {detailProd.attributeSchema?.length > 0 && (
                  <button style={S.btn()} onClick={() => generateVariants(detailProd._id)}>↻ Regenerate</button>
                )}
              </div>
              {detailProd.variants?.length === 0 ? (
                <div style={{ padding:24, textAlign:"center", color:"#9ca3af" }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>🎨</div>
                  No variants yet
                  {detailProd.attributeSchema?.length > 0
                    ? <div><button style={{ ...S.btn(), marginTop:12 }} onClick={() => generateVariants(detailProd._id)}>Generate Variants</button></div>
                    : <div style={{ fontSize:12, marginTop:8 }}>Edit product to add attributes first</div>}
                </div>
              ) : (
                <div style={{ overflowX:"auto", maxHeight:400 }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>
                      <th style={S.th}>SKU</th>
                      <th style={S.th}>Attributes</th>
                      <th style={S.th}>Price</th>
                      <th style={S.th}>Stock</th>
                    </tr></thead>
                    <tbody>
                      {detailProd.variants.map((v, i) => (
                        <tr key={i} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:11 }}>{v.sku}</td>
                          <td style={S.td}>
                            <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                              {Object.entries(v.attributes||{}).map(([k,val]) => (
                                <span key={k} style={{ fontSize:10, background:"#f3f4f6", padding:"2px 6px", borderRadius:4 }}>{k}: {val}</span>
                              ))}
                            </div>
                          </td>
                          <td style={{ ...S.td, fontWeight:600 }}>{INR(v.price)}</td>
                          <td style={{ ...S.td, color:v.stock===0?"#dc2626":"#111", fontWeight:600 }}>{v.stock}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* description */}
            {detailProd.description && (
              <div style={S.card}>
                <div style={S.cardHdr}><span>Description</span></div>
                <div style={{ padding:16, fontSize:13, color:"#374151", lineHeight:1.7 }}>{detailProd.description}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ BULK UPLOAD TAB ══ */}
      {tab==="bulk" && (
        <div style={{ maxWidth:700 }}>
          <div style={S.card}>
            <div style={S.cardHdr}><span>📊 Bulk Upload via Excel / CSV</span></div>
            <div style={{ padding:20 }}>
              {/* template download */}
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:14, marginBottom:16 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:6 }}>📋 Download Excel Template</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:10 }}>
                  Your Excel file should have these columns:
                </div>
                <div style={{ fontFamily:"monospace", fontSize:11, background:"#fff", padding:10, borderRadius:6, border:"1px solid #e5e7eb", overflowX:"auto", whiteSpace:"nowrap" }}>
                  name | brand | category | subCategory | description | hsn | gstRate | basePrice | mrp | costPrice | shopifyPrice | amazonPrice | flipkartPrice | myntraPrice | weight | sku | stock | imageUrl | tags
                </div>
                <button style={{ ...S.btn("#15803d"), marginTop:10, fontSize:12 }} onClick={() => {
                  const csv = "name,brand,category,subCategory,description,hsn,gstRate,basePrice,mrp,costPrice,shopifyPrice,amazonPrice,flipkartPrice,myntraPrice,weight,sku,stock,imageUrl,tags\n" +
                    "Black Leather Belt,Leather Patta,Belts,Formal,Premium full-grain leather belt,42033000,18,999,1499,400,999,1049,989,1099,150,LP-BLK-001,50,,black belt leather formal\n";
                  const a = document.createElement("a"); a.href = "data:text/csv," + encodeURIComponent(csv); a.download = "sellsync_pim_template.csv"; a.click();
                  toast("Template downloaded ✓", "success");
                }}>⬇ Download Template</button>
              </div>

              {/* file upload */}
              <label style={S.label}>Upload Excel or CSV file</label>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkFile}
                style={{ marginBottom:16, fontSize:13, display:"block" }}/>

              {/* preview */}
              {bulkData.length > 0 && (
                <>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:8, color:"#15803d" }}>
                    ✓ {bulkData.length} products ready to import
                  </div>
                  <div style={{ overflowX:"auto", maxHeight:250, marginBottom:16 }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                      <thead><tr>
                        {Object.keys(bulkData[0]).slice(0,6).map((h) => <th key={h} style={{ ...S.th, fontSize:10 }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {bulkData.slice(0,5).map((row, i) => (
                          <tr key={i}>
                            {Object.values(row).slice(0,6).map((v,j) => <td key={j} style={{ ...S.td, fontSize:11 }}>{String(v||"").slice(0,30)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkData.length > 5 && <div style={{ textAlign:"center", padding:8, fontSize:12, color:"#9ca3af" }}>...and {bulkData.length-5} more rows</div>}
                  </div>
                  <div style={{ display:"flex", gap:10 }}>
                    <button style={{ ...S.btn(), flex:1, padding:11, fontSize:14, opacity:uploading?0.7:1 }} onClick={submitBulk} disabled={uploading}>
                      {uploading ? "Uploading…" : "⬆ Import " + bulkData.length + " Products"}
                    </button>
                    <button style={S.btnOut} onClick={() => setBulkData([])}>Clear</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ CREATE / EDIT MODAL ══ */}
      {showModal && (
        <div style={S.overlay} onClick={(e) => e.target===e.currentTarget && setShowModal(false)}>
          <div style={S.modal}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>{editProduct ? "Edit Product" : "New Product"}</span>
              <button onClick={() => setShowModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>

            {/* inner tabs */}
            <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16, gap:0 }}>
              {[{id:"basic",label:"Basic"},{id:"pricing",label:"Pricing"},{id:"attributes",label:"Attributes"},{id:"images",label:"Images"}].map((t) => (
                <button key={t.id} style={{ padding:"8px 16px", background:"none", border:"none", borderBottom:activeTab===t.id?"2px solid #F47920":"2px solid transparent", color:activeTab===t.id?"#F47920":"#6b7280", fontWeight:activeTab===t.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit" }} onClick={() => setActiveTab(t.id)}>{t.label}</button>
              ))}
            </div>

            {/* basic tab */}
            {activeTab==="basic" && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={S.label}>Product Name *</label>
                  <input style={S.input} placeholder="e.g. Black Genuine Leather Formal Belt" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>Brand</label>
                  <input style={S.input} placeholder="e.g. Leather Patta" value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>Category</label>
                  <select style={S.input} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category:e.target.value }))}>
                    {categories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Sub-category</label>
                  <input style={S.input} placeholder="e.g. Formal Belts" value={form.subCategory} onChange={(e) => setForm((f) => ({ ...f, subCategory:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>HSN Code</label>
                  <input style={S.input} placeholder="42033000" value={form.hsn} onChange={(e) => setForm((f) => ({ ...f, hsn:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>GST Rate</label>
                  <select style={S.input} value={form.gstRate} onChange={(e) => setForm((f) => ({ ...f, gstRate:parseInt(e.target.value) }))}>
                    {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={S.label}>Short Description</label>
                  <input style={S.input} placeholder="One-line product description" value={form.shortDesc} onChange={(e) => setForm((f) => ({ ...f, shortDesc:e.target.value }))}/>
                </div>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={S.label}>Full Description</label>
                  <textarea style={{ ...S.input, height:80, resize:"vertical" }} placeholder="Detailed product description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description:e.target.value }))}/>
                </div>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={S.label}>Tags (comma separated)</label>
                  <input style={S.input} placeholder="leather belt, formal belt, mens belt" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags:e.target.value }))}/>
                </div>
                <div><label style={S.label}>Weight (grams)</label><input type="number" style={S.input} value={form.weight} onChange={(e) => setForm((f) => ({ ...f, weight:parseFloat(e.target.value)||0 }))}/></div>
                <div><label style={S.label}>Country of Origin</label><input style={S.input} value={form.countryOfOrigin||"India"} onChange={(e) => setForm((f) => ({ ...f, countryOfOrigin:e.target.value }))}/></div>
                <div><label style={S.label}>Length (cm)</label><input type="number" style={S.input} value={form.length} onChange={(e) => setForm((f) => ({ ...f, length:parseFloat(e.target.value)||0 }))}/></div>
                <div><label style={S.label}>Breadth (cm)</label><input type="number" style={S.input} value={form.breadth} onChange={(e) => setForm((f) => ({ ...f, breadth:parseFloat(e.target.value)||0 }))}/></div>
                <div><label style={S.label}>Height (cm)</label><input type="number" style={S.input} value={form.height} onChange={(e) => setForm((f) => ({ ...f, height:parseFloat(e.target.value)||0 }))}/></div>
                <div><label style={S.label}>Notes</label><input style={S.input} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes:e.target.value }))}/></div>
              </div>
            )}

            {/* pricing tab */}
            {activeTab==="pricing" && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                <div>
                  <label style={S.label}>Base / Selling Price (₹)</label>
                  <input type="number" style={S.input} value={form.basePrice} onChange={(e) => { const v=parseFloat(e.target.value)||0; setForm((f) => ({ ...f, basePrice:v, channelPricing:{ shopify:v, amazon:Math.round(v*1.05), flipkart:Math.round(v*1.04), myntra:Math.round(v*1.06) } })); }}/>
                </div>
                <div>
                  <label style={S.label}>MRP (₹)</label>
                  <input type="number" style={S.input} value={form.mrp} onChange={(e) => setForm((f) => ({ ...f, mrp:parseFloat(e.target.value)||0 }))}/>
                </div>
                <div>
                  <label style={S.label}>Cost Price (₹)</label>
                  <input type="number" style={S.input} value={form.costPrice} onChange={(e) => setForm((f) => ({ ...f, costPrice:parseFloat(e.target.value)||0 }))}/>
                </div>
                {form.basePrice > 0 && form.costPrice > 0 && (
                  <div style={{ gridColumn:"1/-1", background:"#f0fdf4", borderRadius:8, padding:12, fontSize:13 }}>
                    <span style={{ color:"#6b7280" }}>Gross Margin: </span>
                    <span style={{ fontWeight:700, color:"#15803d" }}>{Math.round((form.basePrice-form.costPrice)/form.basePrice*100)}%</span>
                    <span style={{ color:"#6b7280", marginLeft:16 }}>Profit per unit: </span>
                    <span style={{ fontWeight:700, color:"#15803d" }}>{INR(form.basePrice-form.costPrice)}</span>
                  </div>
                )}
                <div style={{ gridColumn:"1/-1" }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Per-channel Pricing</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }}>
                    {PLATFORMS.map((pl) => (
                      <div key={pl.id} style={{ background:pl.bg, borderRadius:8, padding:10, border:`1px solid ${pl.color}30` }}>
                        <label style={{ ...S.label, color:pl.color }}>{pl.name} Price (₹)</label>
                        <input type="number" style={{ ...S.input, marginBottom:0 }} value={form.channelPricing?.[pl.id]||form.basePrice}
                          onChange={(e) => setForm((f) => ({ ...f, channelPricing:{ ...f.channelPricing, [pl.id]:parseFloat(e.target.value)||0 } }))}/>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* attributes tab */}
            {activeTab==="attributes" && (
              <div>
                <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:12, marginBottom:14, fontSize:12, color:"#92400e" }}>
                  💡 Define attributes like Size, Color, Material. Variants will be auto-generated from all combinations.
                </div>

                {/* quick add presets for belts */}
                <div style={{ marginBottom:14 }}>
                  <label style={S.label}>Quick Add Preset Attributes</label>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {BELT_ATTRS.map((attr) => (
                      <button key={attr.name} style={{ ...S.btnOut, fontSize:11 }} onClick={() => addAttr(attr)}>+ {attr.name}</button>
                    ))}
                    <button style={{ ...S.btnOut, fontSize:11 }} onClick={() => addAttr(null)}>+ Custom</button>
                  </div>
                </div>

                {(form.attributeSchema||[]).map((attr, i) => (
                  <div key={i} style={{ background:"#f9fafb", borderRadius:8, padding:12, marginBottom:10, border:"1px solid #e5e7eb" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                      <input style={{ ...S.input, marginBottom:0, width:180, fontWeight:600 }} placeholder="Attribute name (e.g. Size)" value={attr.name}
                        onChange={(e) => updateAttr(i, "name", e.target.value)}/>
                      <button onClick={() => removeAttr(i)} style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:16 }}>✕</button>
                    </div>
                    <label style={S.label}>Values (comma separated)</label>
                    <input style={{ ...S.input, marginBottom:4 }} placeholder="e.g. S, M, L, XL, Free Size"
                      value={(attr.values||[]).join(", ")}
                      onChange={(e) => updateAttr(i, "values", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}/>
                    <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                      {(attr.values||[]).map((v) => <span key={v} style={{ background:"#F47920", color:"#fff", padding:"2px 8px", borderRadius:20, fontSize:11 }}>{v}</span>)}
                    </div>
                  </div>
                ))}

                {(form.attributeSchema||[]).length > 0 && (
                  <div style={{ background:"#f0fdf4", borderRadius:8, padding:12, marginTop:8, fontSize:13, color:"#15803d" }}>
                    ✓ Will generate <strong>{(form.attributeSchema||[]).reduce((a,attr) => a*(attr.values?.length||1), 1)}</strong> variants automatically
                    <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, cursor:"pointer" }}>
                      <input type="checkbox" checked={form.autoGenerateVariants} onChange={(e) => setForm((f) => ({ ...f, autoGenerateVariants:e.target.checked }))}/>
                      <span style={{ fontSize:12 }}>Auto-generate variants on save</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* images tab */}
            {activeTab==="images" && (
              <div>
                <label style={S.label}>Image URLs (one per line)</label>
                <textarea style={{ ...S.input, height:150, resize:"vertical", fontFamily:"monospace", fontSize:12 }}
                  placeholder={"https://example.com/image1.jpg\nhttps://example.com/image2.jpg"}
                  value={form.images} onChange={(e) => setForm((f) => ({ ...f, images:e.target.value }))}/>
                {form.images && (
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:8 }}>
                    {form.images.split("\n").filter((u) => u.trim()).map((url, i) => (
                      <div key={i} style={{ position:"relative" }}>
                        <img src={url.trim()} alt="" style={{ width:80, height:80, borderRadius:8, objectFit:"cover", border:"1px solid #e5e7eb" }}
                          onError={(e) => e.target.style.display="none"}/>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize:12, color:"#9ca3af", marginTop:10 }}>
                  Paste image URLs from your Shopify store, Google Drive, or any CDN. First image will be used as the main product image.
                </div>
              </div>
            )}

            <div style={{ marginTop:20, display:"flex", gap:10 }}>
              <button style={{ ...S.btn(), flex:1, padding:11, fontSize:14 }} onClick={saveProduct}>
                {editProduct ? "Update Product" : "Create Product"}
              </button>
              <button style={S.btnOut} onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ AI CATALOG GENERATOR ══ */}
      {tab === "ai_catalog" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"380px 1fr", gap:16, alignItems:"start" }}>

            {/* Left — Upload + Config */}
            <div>
              {/* Image upload */}
              <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14, overflow:"hidden" }}>
                <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>📸 Product Image</div>
                <div style={{ padding:16 }}>
                  <div
                    style={{ border:"2px dashed #e5e7eb", borderRadius:8, padding:aiImage?"8px":"40px 20px", textAlign:"center", cursor:"pointer", background:"#fafafa", marginBottom:10, transition:"all .15s" }}
                    onClick={() => document.getElementById("ai-image-input").click()}
                    onDragOver={(e)=>e.preventDefault()}
                    onDrop={(e)=>{e.preventDefault();handleAiImageUpload(e.dataTransfer.files[0]);}}>
                    <input id="ai-image-input" type="file" accept="image/*" style={{ display:"none" }} onChange={(e)=>handleAiImageUpload(e.target.files[0])}/>
                    {aiImage ? (
                      <img src={aiImage.preview} alt="product" style={{ width:"100%", maxHeight:240, objectFit:"contain", borderRadius:6 }}/>
                    ) : (
                      <>
                        <div style={{ fontSize:36, marginBottom:8 }}>📸</div>
                        <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>Drop product image here</div>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>PNG, JPG, WEBP · any size</div>
                      </>
                    )}
                  </div>
                  {aiImage && (
                    <button style={{ ...S.btnOut, width:"100%", fontSize:11 }} onClick={()=>{setAiImage(null);setAiResult(null);}}>✕ Remove image</button>
                  )}
                </div>
              </div>

              {/* Extra product info */}
              <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 }}>
                <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>ℹ️ Product Details (Optional)</div>
                <div style={{ padding:16, display:"flex", flexDirection:"column", gap:10 }}>
                  {[
                    ["SKU","sku","text","e.g. LP-BLK-001"],
                    ["MRP (₹)","mrp","number","499"],
                    ["Cost Price (₹)","costPrice","number","280"],
                    ["HSN Code","hsn","text","42033000"],
                    ["Category","category","text","Belts"],
                    ["Brand","brand","text","Leather Patta"],
                    ["Color","color","text","Black"],
                    ["Material","material","text","Genuine Leather"],
                  ].map(([label,key,type,ph])=>(
                    <div key={key}>
                      <label style={{ ...S.label }}>{label}</label>
                      <input type={type} style={{ ...S.input }} placeholder={ph} value={extraInfo[key]}
                        onChange={(e)=>setExtraInfo((f)=>({...f,[key]:e.target.value}))}/>
                    </div>
                  ))}
                </div>
              </div>

              {/* Platform selector */}
              <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 }}>
                <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>🌐 Generate For</div>
                <div style={{ padding:14, display:"flex", flexDirection:"column", gap:8 }}>
                  {[["all","🌐 All Platforms","#1A3A5C"],["shopify","🏪 Shopify","#5a8a00"],["amazon","📦 Amazon","#c45000"],["flipkart","🛒 Flipkart","#1b57c0"],["myntra","👗 Myntra","#b5003c"],["meesho","🛍️ Meesho","#9f2089"]].map(([id,label,color])=>(
                    <button key={id} onClick={()=>setAiPlatform(id)}
                      style={{ padding:"8px 12px", borderRadius:7, border:"2px solid "+(aiPlatform===id?color:"#e5e7eb"), background:aiPlatform===id?color+"18":"#fff", color:aiPlatform===id?color:"#374151", fontWeight:aiPlatform===id?700:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                style={{ ...S.btn("#6366f1"), width:"100%", padding:"12px", fontSize:14, opacity:(!aiImage||aiLoading)?0.6:1 }}
                onClick={generateCatalog} disabled={!aiImage||aiLoading}>
                {aiLoading ? "🤖 Generating…" : "🤖 Generate Catalog Content"}
              </button>
            </div>

            {/* Right — Results */}
            <div>
              {!aiImage && !aiResult && (
                <div style={{ background:"#fff", borderRadius:10, border:"2px dashed #e5e7eb", padding:60, textAlign:"center" }}>
                  <div style={{ fontSize:52, marginBottom:16 }}>🤖</div>
                  <div style={{ fontWeight:700, fontSize:18, marginBottom:8, color:"#1A3A5C" }}>AI Catalog Generator</div>
                  <div style={{ fontSize:13, color:"#6b7280", maxWidth:380, margin:"0 auto 8px" }}>
                    Upload any product image → AI generates optimised title, description, keywords and HSN code for every platform automatically.
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:20, textAlign:"left", maxWidth:360, margin:"20px auto 0" }}>
                    {[["🏪 Shopify","SEO title, storytelling description, meta tags"],["📦 Amazon","Bullet features, search keywords, compliance"],["🛒 Flipkart","Spec-heavy, technical details, category mapping"],["👗 Myntra","Fashion-forward, occasion-based copy"],["🛍️ Meesho","Simple, value-focused, reseller-friendly"],["🏷️ HSN Code","Auto-detects correct GST HSN code"]].map(([t,d])=>(
                      <div key={t} style={{ background:"#f9fafb", borderRadius:8, padding:"10px 12px" }}>
                        <div style={{ fontWeight:700, fontSize:12, marginBottom:3 }}>{t}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{d}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiLoading && (
                <div style={{ background:"#fff", borderRadius:10, border:"2px solid #6366f1", padding:60, textAlign:"center" }}>
                  <div style={{ fontSize:48, marginBottom:16, animation:"spin 2s linear infinite" }}>🤖</div>
                  <div style={{ fontWeight:700, fontSize:16, color:"#6366f1", marginBottom:8 }}>AI is analysing your product…</div>
                  <div style={{ fontSize:12, color:"#9ca3af" }}>Generating optimised content for {aiPlatform==="all"?"all 5 platforms":aiPlatform}</div>
                </div>
              )}

              {!aiLoading && aiResult && (() => {
                const analysis = aiResult.productAnalysis;
                const platforms = aiResult.platforms || {};
                const PLT_COLORS = { shopify:"#5a8a00", amazon:"#c45000", flipkart:"#1b57c0", myntra:"#b5003c", meesho:"#9f2089" };
                const PLT_ICONS  = { shopify:"🏪", amazon:"📦", flipkart:"🛒", myntra:"👗", meesho:"🛍️" };

                return (
                  <div>
                    {/* Product Analysis */}
                    <div style={{ background:"linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius:10, padding:"16px 18px", marginBottom:14, color:"#fff" }}>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:8 }}>🔍 AI Product Analysis</div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:10 }}>
                        {[
                          ["Detected","detecteProduct",analysis?.detectedProduct],
                          ["Color",   "color",          analysis?.color],
                          ["Material","material",       analysis?.material],
                          ["Style",   "style",          analysis?.style],
                          ["HSN Code","hsn",            analysis?.suggestedHsn],
                          ["Category","cat",            analysis?.suggestedCategory],
                        ].map(([l,,v])=>v?(
                          <div key={l} style={{ background:"rgba(255,255,255,0.15)", borderRadius:6, padding:"7px 10px" }}>
                            <div style={{ fontSize:9, opacity:.8, textTransform:"uppercase", marginBottom:2 }}>{l}</div>
                            <div style={{ fontWeight:600, fontSize:11 }}>{v}</div>
                          </div>
                        ):null)}
                      </div>
                      {analysis?.keyFeatures?.length > 0 && (
                        <div>
                          <div style={{ fontSize:10, opacity:.8, marginBottom:5 }}>KEY FEATURES DETECTED</div>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                            {analysis.keyFeatures.map((f,i)=>(
                              <span key={i} style={{ background:"rgba(255,255,255,0.2)", borderRadius:20, padding:"2px 9px", fontSize:10 }}>{f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Save button */}
                    <div style={{ display:"flex", gap:8, marginBottom:14, justifyContent:"flex-end" }}>
                      <button style={{ ...S.btn("#15803d") }} onClick={saveToProduct}>💾 Save to Product Catalog</button>
                      <button style={{ ...S.btn("#6366f1") }} onClick={generateCatalog}>↻ Regenerate</button>
                    </div>

                    {/* Platform content cards */}
                    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                      {Object.entries(platforms).map(([plt, data]) => {
                        const color = PLT_COLORS[plt] || "#6b7280";
                        const icon  = PLT_ICONS[plt]  || "🛒";
                        return (
                          <div key={plt} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                            {/* Platform header */}
                            <div style={{ background:color, padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                              <span style={{ fontWeight:700, fontSize:14, color:"#fff" }}>{icon} {plt.charAt(0).toUpperCase()+plt.slice(1)}</span>
                              <button onClick={()=>copyToClipboard(JSON.stringify(data,null,2))}
                                style={{ background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", padding:"3px 10px", borderRadius:5, cursor:"pointer", fontSize:11 }}>
                                Copy All
                              </button>
                            </div>

                            <div style={{ padding:14, display:"flex", flexDirection:"column", gap:12 }}>
                              {/* Title */}
                              <div>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                                  <label style={{ ...S.label, marginBottom:0 }}>Title ({(data.title||"").length} chars)</label>
                                  <button onClick={()=>copyToClipboard(data.title)} style={{ ...S.btnOut, padding:"1px 8px", fontSize:10 }}>Copy</button>
                                </div>
                                <div style={{ background:"#f9fafb", borderRadius:6, padding:"8px 10px", fontSize:12, fontWeight:600, color:"#1A3A5C", border:"1px solid #e5e7eb" }}>
                                  {data.title}
                                </div>
                              </div>

                              {/* Description */}
                              <div>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                                  <label style={{ ...S.label, marginBottom:0 }}>Description</label>
                                  <button onClick={()=>copyToClipboard(data.description)} style={{ ...S.btnOut, padding:"1px 8px", fontSize:10 }}>Copy</button>
                                </div>
                                <div style={{ background:"#f9fafb", borderRadius:6, padding:"8px 10px", fontSize:11, color:"#374151", border:"1px solid #e5e7eb", lineHeight:1.6, maxHeight:120, overflowY:"auto" }}>
                                  {data.description}
                                </div>
                              </div>

                              {/* Bullet Points */}
                              {data.bulletPoints?.length > 0 && (
                                <div>
                                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                                    <label style={{ ...S.label, marginBottom:0 }}>Key Features / Bullet Points</label>
                                    <button onClick={()=>copyToClipboard(data.bulletPoints.join("\n"))} style={{ ...S.btnOut, padding:"1px 8px", fontSize:10 }}>Copy</button>
                                  </div>
                                  <div style={{ background:"#f9fafb", borderRadius:6, padding:"8px 10px", border:"1px solid #e5e7eb" }}>
                                    {data.bulletPoints.map((b,i)=>(
                                      <div key={i} style={{ fontSize:11, color:"#374151", display:"flex", gap:6, marginBottom:4 }}>
                                        <span style={{ color, fontWeight:700, flexShrink:0 }}>•</span>{b}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Keywords */}
                              {data.keywords?.length > 0 && (
                                <div>
                                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                                    <label style={{ ...S.label, marginBottom:0 }}>Search Keywords ({data.keywords.length})</label>
                                    <button onClick={()=>copyToClipboard(data.keywords.join(", "))} style={{ ...S.btnOut, padding:"1px 8px", fontSize:10 }}>Copy</button>
                                  </div>
                                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                                    {data.keywords.map((k,i)=>(
                                      <span key={i} style={{ background:color+"18", color, padding:"2px 9px", borderRadius:20, fontSize:10, fontWeight:600 }}>{k}</span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Meta */}
                              {(data.metaTitle||data.metaDescription) && (
                                <div style={{ background:"#f0fdf4", borderRadius:6, padding:"8px 10px", border:"1px solid #bbf7d0" }}>
                                  <div style={{ fontSize:10, fontWeight:700, color:"#15803d", marginBottom:4 }}>SEO META TAGS</div>
                                  {data.metaTitle && <div style={{ fontSize:11, fontWeight:600, marginBottom:2 }}>Title: {data.metaTitle}</div>}
                                  {data.metaDescription && <div style={{ fontSize:11, color:"#6b7280" }}>Desc: {data.metaDescription}</div>}
                                  <button onClick={()=>copyToClipboard(`Meta Title: ${data.metaTitle}
Meta Description: ${data.metaDescription}`)}
                                    style={{ ...S.btnOut, marginTop:6, padding:"2px 8px", fontSize:10 }}>Copy Meta</button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
